// Holds the Supabase session + the user's profile (role, reseller_name).
import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { api } from '../lib/api';
import { refuseInDemo } from '../config/demo';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // True when the server says this session still needs the emailed 2FA code.
  const [mfaRequired, setMfaRequired] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (!s) setProfile(null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Load profile whenever a session appears. If the server answers 'mfa_required',
  // the user must enter the emailed code before anything else loads.
  useEffect(() => {
    if (!session) { setMfaRequired(false); return; }
    api.get('/api/auth/me')
      .then((p) => { setProfile(p); setMfaRequired(false); })
      .catch((err) => {
        setProfile(null);
        if (err.code === 'mfa_required') setMfaRequired(true);
      });
  }, [session]);

  // Re-fetch the profile (after the user edits their own details).
  const refreshProfile = async () => {
    const p = await api.get('/api/auth/me');
    setProfile(p);
    return p;
  };

  const login = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setMfaRequired(false);
  };

  // Authenticator-app 2FA: is it set up yet, and if not, start the QR enrollment.
  const getMfaStatus = () => api.get('/api/auth/mfa/status');
  const enrollMfa = () => api.post('/api/auth/mfa/enroll');

  // Submit the app's 6-digit code; on success the session is fully unlocked.
  const verifyMfa = async (code) => {
    await api.post('/api/auth/mfa/verify', { code });
    setMfaRequired(false);
    const p = await api.get('/api/auth/me');
    setProfile(p);
  };

  // Send a password-reset email; the link returns the user to /reset-password.
  // Refused in demo mode — this would put a real email in a real user's inbox.
  const sendPasswordReset = async (email) => {
    refuseInDemo('password-reset');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw new Error(error.message);
  };

  // Set a new password for the current (recovery) session.
  // Refused in demo mode — this would change a real account's real password.
  const updatePassword = async (password) => {
    refuseInDemo('password-change');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw new Error(error.message);
  };

  return (
    <AuthContext.Provider value={{ session, profile, loading, mfaRequired, getMfaStatus, enrollMfa, verifyMfa, login, logout, sendPasswordReset, updatePassword, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => useContext(AuthContext);
